import { getUserSetting, setUserSetting } from '@lib/settings/userSettings';
import { trackingStore, type LibraryPinControl } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';

const FAVORITE_LIBRARIES_KEY = 'favoriteLibraries';

/**
 * Which libraries the user has pinned, app-wide.
 *
 * A singleton (not a context store) for the same reason as `OmniFilterPreferences`:
 * `LibraryManager.sortedAndFilteredLibraries` hoists pinned libraries to the top, and a
 * MobX computed can only read pin state reactively if it lives outside the React tree.
 * This state used to be a `useState` inside `useFavoriteLibraries`, instantiated twice
 * independently (Libraries page + Workspace); that hook is now a thin adapter over this
 * store, so both surfaces share one truth.
 *
 * Persistence is unchanged — `get/setUserSetting` under the `favoriteLibraries` key.
 * Deliberately NOT moved into `UIPreferences.ownedSettings()`: `/user/me/settings` takes
 * a whole settings document, and adding a third writer of it re-opens the clobber bug
 * documented at `UIPreferences.ts:245-275`.
 *
 * Hydration is lazy (`ensureHydrated`), so this costs nothing at app boot.
 */
export class LibraryFavoritesStore {
  @observable
  private _favoriteIds: string[] = [];

  @observable
  private _hydrated = false;

  /** Dedupes concurrent `ensureHydrated()` calls (both consumer hooks can mount at once). */
  private hydrating: Promise<void> | null = null;

  /**
   * Bumped by `reset()` to abandon a hydration still in flight. Without it, a logout landing
   * between the settings request and its response would let the resolving read write the
   * previous session's pins back in — and flip `_hydrated` true in its `finally` — after the
   * reset had cleared them, which is exactly the leak `reset()` exists to prevent.
   *
   * Observable, so consumers can key a re-hydration effect on it. `reset()` alone isn't enough
   * of a signal: it sets `_hydrated = false`, which is a no-op for MobX when the reset lands
   * *during* an in-flight hydration (the flag is already false), so nothing would notify and
   * nothing would retry — leaving `hydrated` stuck false and any surface gating first paint on it
   * blank forever.
   */
  @observable
  private hydrationRun = 0;

  private crossStoreReactionsBound = false;

  constructor() {
    // No store reads here — `userStore` is behind an import cycle and reading it from a
    // module-scope constructor hits the TDZ / chunk-order hazard documented in
    // `userSettings.ts:25-42`. The logout reaction is bound from
    // `initCrossStoreReactions()` instead.
    makeObservable(this);
  }

  /** Pinned library ids, most recently pinned first. */
  @computed
  get favoriteIds() {
    return this._favoriteIds.slice();
  }

  @computed
  get favoriteIdSet() {
    return new Set(this._favoriteIds);
  }

  /** False until the stored list has been read back (or the read has failed). */
  @computed
  get hydrated() {
    return this._hydrated;
  }

  /**
   * Increments every time the hydration lifecycle restarts (i.e. on `reset()`). Pair it with
   * `hydrated` in a re-hydration effect's dependencies: together they change on every transition
   * that should trigger a fresh read, including a reset that lands mid-flight.
   */
  @computed
  get hydrationEpoch() {
    return this.hydrationRun;
  }

  isFavorited(libraryId: string) {
    return this.favoriteIdSet.has(libraryId);
  }

  /**
   * Replaces the list, bailing out when nothing actually changed.
   *
   * The bail-out is load-bearing, not an optimisation: `libraryManager.libraries` is a
   * computed that yields a fresh array whenever any library's data changes, so the prune
   * path re-fires repeatedly while stats stream in on load. Without it, every pass sets a
   * new array identity and re-renders every consumer — which for the Libraries table
   * means MUI rebuilding its column state (and discarding the user's column resizes) on
   * each one.
   *
   * Assign, never mutate in place: an observable array keeps its identity across
   * `push`/`splice`, so only assignment makes the derived `favoriteIds`/`favoriteIdSet`
   * identities change and consumers' `useMemo`s invalidate.
   */
  @action
  setFavoriteIds(next: string[]) {
    const current = this._favoriteIds;
    const unchanged = next.length === current.length && next.every((id, i) => id === current[i]);
    if (unchanged) return;
    this._favoriteIds = next;
  }

  /**
   * Pins (newest first) or unpins one library, then persists the new list.
   *
   * The local update is optimistic and is *not* rolled back if the write fails — the pin will be
   * missing after a reload. That's inherited behaviour, not new, but the failure is at least no
   * longer an unhandled rejection: `UserSettingsService.set` rethrows on any POST failure, so a
   * bare `void` left the browser reporting it with no context. Surfacing it to the user needs a
   * toast template (see `.claude/rules/toastmaster.md`), which is a product call.
   *
   * `control` is required rather than optional so a new pin surface can't be added without saying
   * where it is — the whole point of the event is telling the card's hover pin apart from the
   * table's column. Tracking lives here, not at the call sites, for the same reason.
   */
  @action
  toggleFavorite(libraryId: string, control: LibraryPinControl) {
    const prev = this._favoriteIds;
    const pinned = !prev.includes(libraryId);
    const next = pinned ? [libraryId, ...prev] : prev.filter((id) => id !== libraryId);
    this._favoriteIds = next;
    void this.persist(next, 'toggle');
    trackingStore.libraryPinToggled({ pinned, control, libraryId });
  }

  /** Single place the pin list is written, so no call site can forget to handle a rejection. */
  private async persist(ids: string[], reason: 'toggle' | 'prune') {
    try {
      await setUserSetting(FAVORITE_LIBRARIES_KEY, ids);
    } catch (error) {
      console.error(`Failed to save pinned libraries (${reason}):`, error);
    }
  }

  /**
   * Reads the stored list once. Safe to call from every consumer's mount effect — the
   * in-flight promise is shared, and `_hydrated` flips in a `finally` so a failed
   * settings read can't wedge a surface that gates its first paint on it.
   */
  async ensureHydrated() {
    if (this._hydrated) return;
    if (this.hydrating) return this.hydrating;

    const run = this.hydrationRun;
    // Every write below is gated on the run still being current. A stale run must not null
    // `hydrating` either: `reset()` already did, and a newer hydration may have replaced it.
    this.hydrating = (async () => {
      try {
        const stored = await getUserSetting<string[]>(FAVORITE_LIBRARIES_KEY, []);
        if (run !== this.hydrationRun) return;
        this.setFavoriteIds(stored ?? []);
      } finally {
        if (run === this.hydrationRun) {
          runInAction(() => {
            this._hydrated = true;
          });
          this.hydrating = null;
        }
      }
    })();

    return this.hydrating;
  }

  /**
   * Drops pins pointing at libraries that no longer exist, persisting only when
   * something was actually removed. A no-op before hydration, so it can't race the read
   * and write an empty list back.
   *
   * Callers must pass ids from `LibraryManager.knownLibraryIds`, not `libraries` — the latter
   * hides a library whose `data` was nulled by a failed refresh, and this method *persists*
   * what it removes, so a transient network blip would otherwise delete the pin for good.
   *
   * An empty set is likewise read as "we don't know what exists yet" rather than "everything
   * was deleted", because it's what an unloaded manager looks like. The cost is that deleting
   * your very last library leaves its pin behind until another library exists — harmless, since
   * a pin for a missing library resolves to nothing everywhere it's read.
   */
  async pruneMissing(existingIds: Set<string>) {
    if (!this._hydrated) return;
    if (existingIds.size === 0) return;
    const current = this._favoriteIds;
    const cleaned = current.filter((id) => existingIds.has(id));
    if (cleaned.length === current.length) return;
    this.setFavoriteIds(cleaned);
    await this.persist(cleaned, 'prune');
  }

  @action
  reset() {
    this._favoriteIds = [];
    this._hydrated = false;
    // Abandon any read still in flight, so it can't land after this and restore the pins. The
    // bump is also the only observable change a mid-flight reset produces — see `hydrationRun`.
    this.hydrationRun += 1;
    this.hydrating = null;
  }

  /**
   * Clear pins when the session ends. Unlike the old hook state, this store outlives the
   * component tree — without this, signing into a second account would show the first
   * account's pins until a reload.
   *
   * Bound from `initCrossStoreReactions()`, never the constructor (see above). No
   * `fireImmediately`: hydration stays lazy.
   */
  initCrossStoreReactions() {
    // Idempotence guard, spelled the same way as `DebugStore`'s — `initCrossStoreReactions()`
    // runs once from `index.tsx` today, but a second call must not stack a duplicate reaction.
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;
    reaction(
      () => userStore.isLoggedIn,
      () => this.reset(),
    );
  }
}

export const libraryFavorites = new LibraryFavoritesStore();
